import React, { useEffect, useRef, useState } from 'react';

interface Props {
  url: string;
  alt?: string;
  fit?: string;
  posX?: number;
  posY?: number;
  posScale?: number;
  radius?: string;
  className?: string;
  wrapperStyle?: React.CSSProperties;
  imgStyle?: React.CSSProperties;
  /** Background of the wrapper (shown around image when smaller than frame). Default transparent. */
  background?: string;
}

/**
 * Image with cover-fit + free pan + zoom. Position is interpreted as
 * "image center as % of container". Range is unbounded — the image can
 * be positioned anywhere, including partially or fully outside the frame.
 *
 * Falls back to plain object-fit when fit !== 'cover'.
 */
export const PannedImage: React.FC<Props> = ({
  url, alt, fit = 'cover',
  posX = 50, posY = 50, posScale = 1,
  radius, className, wrapperStyle, imgStyle, background,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [container, setContainer] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    // offsetWidth/offsetHeight report the element's own CSS box and are NOT
    // affected by `transform: scale()` on ancestors (unlike
    // getBoundingClientRect, which returns post-transform pixels). The public
    // campaign page wraps the canvas in a uniform scale transform — using
    // offsetWidth/Height here keeps the image crop identical between the
    // editor preview and the public page.
    const update = () => {
      setContainer({ w: el.offsetWidth, h: el.offsetHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const advanced = fit === 'cover' && natural.w > 0 && container.w > 0;
  let img: React.CSSProperties;

  if (advanced) {
    const naturalRatio = natural.w / natural.h;
    const containerRatio = container.w / container.h;
    const baseScale = naturalRatio > containerRatio
      ? container.h / natural.h
      : container.w / natural.w;
    const scale = baseScale * (posScale || 1);
    const W = natural.w * scale;
    const H = natural.h * scale;
    // posX/posY = image center as % of container (50 = centered, < 50 left, > 50 right). Unbounded.
    const left = (posX / 100) * container.w - W / 2;
    const top = (posY / 100) * container.h - H / 2;
    img = {
      position: 'absolute',
      width: W,
      height: H,
      left,
      top,
      borderRadius: radius,
      maxWidth: 'none',
      maxHeight: 'none',
      ...imgStyle,
    };
  } else {
    img = {
      width: '100%',
      height: '100%',
      objectFit: fit as any,
      objectPosition: `${posX}% ${posY}%`,
      borderRadius: radius,
      ...imgStyle,
    };
  }

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        position: 'relative', overflow: 'hidden',
        width: '100%', height: '100%',
        background: background,
        ...wrapperStyle,
      }}
    >
      <img
        src={url}
        alt={alt || ''}
        draggable={false}
        loading="lazy"
        decoding="async"
        onLoad={(e) => {
          const t = e.currentTarget;
          if (t.naturalWidth && t.naturalHeight) {
            setNatural({ w: t.naturalWidth, h: t.naturalHeight });
          }
        }}
        style={img}
      />
    </div>
  );
};
